import { useState, useEffect, useCallback } from 'react';
import { fetchNewsArticles } from '../services/newsArticleService';
import type { NewsArticle } from '../types/newsArticle';

const PAGE_SIZE = 20;

export function useNewsArticles() {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err, hasMore: more } = await fetchNewsArticles(0, PAGE_SIZE);
    if (err) {
      setError(err.message);
      setArticles([]);
      setHasMore(false);
    } else {
      setArticles(data);
      setHasMore(more);
    }
    setLoading(false);
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const { data, error: err, hasMore: more } = await fetchNewsArticles(articles.length, PAGE_SIZE);
    if (err) {
      setError(err.message);
    } else {
      setArticles((prev) => [...prev, ...data]);
      setHasMore(more);
    }
    setLoadingMore(false);
  }, [articles.length, hasMore, loadingMore]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { articles, loading, loadingMore, error, hasMore, refresh, loadMore };
}
